import { ZodError } from 'zod';
import { api, ApiError } from '@/shared/api';
import { isAuthFailure } from '@/shared/auth-failure';
import {
  CreateStorageQuoteResponseSchema,
  StorageGuardadoResponseSchema,
  StorageQuotesInboxResponseSchema,
  StorageQuotesMineResponseSchema,
  SubmitStorageReviewResponseSchema,
  type EndedBy,
  type QuoteStatus,
  type SpaceType,
  type StorageGuardado,
  type StorageQuote,
  type StorageRequestSummary,
  type StorageReview,
  type StorageStatus,
  type StorageType,
  type SubmittedStorageReview
} from '@/contracts/storage-quotes';
import { StorageProfileSchema, type StorageProfile } from '@/contracts/storage-profile';
import { StorageBankAccountResponseSchema } from '@/contracts/storage-bank-account';
import { STORAGE_MODULE_OFF_MESSAGE, STORAGE_REQUESTS_DISABLED_MESSAGE } from '@/shared/storage-module';

// --- Milestone 5 (Solicitudes y Mis cotizaciones) -------------------------------------------------
// Types and calls mirror nexvia-api-web's storage.constants.ts vocabulary and its
// storage-request/storage-quote repositories' `toPlain()` shape exactly (read directly against
// that module's source, 2026-10-13 — the agreed cross-repo contract is roadmap §2 in
// nexvia-api-web; this file is this app's client of it). Keep these in sync with that contract if
// it changes, not with nexvia-api-web's live working tree — see this milestone's own report for
// why that distinction matters here.
//
// The wire shapes themselves now live in `src/contracts/storage-quotes.ts` (Zod schemas, written
// from nexvia-api-web's real handler/repository source, re-confirmed 2026-10-14 — see that file's
// own docblock) — this file re-exports their inferred types below so every existing caller's
// `import { type StorageQuote } from '@/shared/storage-api'` keeps working unchanged, and parses
// every real response through those schemas instead of trusting a hand-written interface with
// nothing checking it at runtime.

// RequestStatus/StorageQuoteRequestSummary (also real, schema-backed exports in
// @/contracts/storage-quotes) are deliberately NOT re-exported here: no existing caller ever
// imported either by name from this module (confirmed by grep, this session) — re-exporting them
// anyway would be unused public surface from day one, exactly what `pnpm deadcode` caught.
export type { SpaceType, StorageType, QuoteStatus, StorageRequestSummary, StorageQuote, StorageGuardado, StorageReview };

export const SPACE_TYPE_LABELS: Record<SpaceType, string> = {
  box_chico: 'Box chico',
  box_mediano: 'Box mediano',
  box_grande: 'Box grande',
  personalizado: 'Espacio personalizado'
};

/**
 * NEX-1165 — decision A9: a storage type is a catalog code (`kind` 'storage_type'); its label comes
 * from the catalog (`getStorageTypeOptions()`). These are only the fallback for the three seeded
 * codes (1790146600000-AddStorageServiceOptionKind.js) when the catalog cannot be read; any other
 * code shows as sent.
 */
export const STORAGE_TYPE_LABELS: Partial<Record<StorageType, string>> = {
  privado: 'Privado',
  compartido: 'Compartido',
  otro: 'Otro'
};

/**
 * NEX-1165 design pass (spec 2026-10-18 §3.3) — the mock's words for a quote's state, from the
 * depósito's side: a pending quote waits for the client's answer, and a rejected one is a quote the
 * client did not pick (the API rejects the siblings when it accepts one), not a judgement on it.
 */
export const QUOTE_STATUS_LABELS: Record<QuoteStatus, string> = {
  pending: 'Esperando respuesta',
  accepted: 'Aceptada',
  rejected: 'No seleccionada',
  expired: 'Vencida',
  cancelled: 'Cancelada'
};

/** NEX-1170 — the guardado's lifecycle, as Mis cotizaciones shows it on the contracted quote. */
export const STORAGE_STATUS_LABELS: Record<StorageStatus, string> = {
  contracted: 'Contratado',
  active: 'Activo',
  ending: 'Terminando',
  ended: 'Terminado'
};

/** NEX-1170 (X1) — how the guardado ended, as this depósito reads it under "Terminó el …". */
export const ENDED_BY_LABELS: Record<EndedBy, string> = {
  storage: 'confirmaste que el cliente retiró sus cosas',
  client: 'el cliente avisó que retiró sus cosas',
  system: 'se cerró automáticamente al terminar el preaviso'
};

/**
 * `POST /storage/requests/:id/quotes` body (nexvia-api-web `createStorageQuoteBody` +
 * `create-storage-quote.handler.ts`, NEX-1165).
 *
 * - `storageLocationId`: one of this depósito's own locations — the address and photos the client
 *   sees next to the quote (business doc §5). Required by the API (422
 *   `STORAGE_QUOTE_LOCATION_REQUIRED` / `STORAGE_QUOTE_LOCATION_INVALID`).
 * - `includedServices`: catalog codes (`GET /storage/service-options`) plus any free-text "otro"
 *   service, stored comma-joined server-side (`included_service_codes`) — so no item may carry a
 *   comma.
 * - `conditions`: only the form's free text; the API puts the minimum months and notice lines in
 *   front of it (`domain/quote-defaults.ts`).
 * - `minContractMonths`/`noticeDays`: this quote's own values. Left out (`undefined`) = the
 *   account's defaults; `null` or 0 = no such condition for this quote.
 * - `validUntil`: a date; `null` = no validity (business doc §3, "Vigencia"); left out = the API
 *   applies the account's `quote_validity_days`.
 */
export interface CreateStorageQuoteInput {
  storageLocationId: number;
  monthlyPrice: number;
  fullPeriodPrice: number;
  includedServices: string[];
  conditions: string | null;
  transportIncluded: boolean;
  validUntil?: string | null;
  minContractMonths?: number | null;
  noticeDays?: number | null;
}

/**
 * Parses a real 2xx response body through `schema`. A 2xx response that does NOT match the
 * contract (a malformed/unexpected real response — the exact shape of bug `ff309b7` fixed, just
 * with a schema catching it this time instead of nothing at all) must never let a raw `ZodError`
 * escape to the calling page: that would crash the page the same way the original
 * `(current ?? []) is not iterable` / `options.map is not a function` bugs did, which is the one
 * failure mode this whole contracts migration exists to prevent, not reintroduce in a new shape.
 * Re-thrown as this module's own `ApiError` (code `CONTRACT_VIOLATION`) so the existing page-level
 * `catch (err) { ... describeStorageError(err) }` handling — which already only knows how to
 * handle an `ApiError` — degrades to the existing error banner instead of an uncaught crash.
 */
function parseResponse<T>(schema: { parse(data: unknown): T }, raw: unknown, endpoint: string): T {
  try {
    return schema.parse(raw);
  } catch (err) {
    if (err instanceof ZodError) {
      throw new ApiError(`Unexpected response shape from ${endpoint}`, 502, 'CONTRACT_VIOLATION');
    }
    throw err;
  }
}

/** `GET /storage/quotes/inbox` — the zone's published requests this depósito has not quoted yet. */
export async function getStorageQuotesInbox(): Promise<StorageRequestSummary[]> {
  const raw = await api.get<unknown>('/storage/quotes/inbox');
  const response = parseResponse(StorageQuotesInboxResponseSchema, raw, 'GET /storage/quotes/inbox');
  return response.data.requests;
}

/** `GET /storage/quotes/mine` — every quote this depósito already sent, with its status. */
export async function getStorageQuotesMine(): Promise<StorageQuote[]> {
  const raw = await api.get<unknown>('/storage/quotes/mine');
  const response = parseResponse(StorageQuotesMineResponseSchema, raw, 'GET /storage/quotes/mine');
  return response.data.quotes;
}

/**
 * `POST /storage/requests/:id/quotes` — the depósito's offer on a request, sent as the form built it
 * (see `CreateStorageQuoteInput`). `currency` is deliberately not a parameter: the API defaults it to
 * `"UYU"` server-side and no milestone models a second currency yet.
 *
 * NEX-1165 — `storageLocationId` comes from the form's location picker (`GET /storage/locations`);
 * it used to be forced to `null` while that route did not exist.
 */
export async function createStorageQuote(requestId: number, input: CreateStorageQuoteInput): Promise<StorageQuote> {
  const raw = await api.post<unknown>(`/storage/requests/${requestId}/quotes`, input);
  const response = parseResponse(CreateStorageQuoteResponseSchema, raw, 'POST /storage/requests/:id/quotes');
  return response.data;
}

// --- Reception of the guardado (NEX-1170, decision A4) --------------------------------------------

/**
 * `POST /storage/requests/:id/confirm-reception { note }` — "Confirmar recepción": contracted →
 * active, the API emails the client. `note` (≤1000, optional) is about the state of the things;
 * `null` when the depósito wrote none. Only the depósito whose quote was accepted, `verified`.
 */
export async function confirmStorageReception(requestId: number, note: string | null): Promise<StorageGuardado> {
  const raw = await api.post<unknown>(`/storage/requests/${requestId}/confirm-reception`, { note });
  return parseResponse(StorageGuardadoResponseSchema, raw, 'POST /storage/requests/:id/confirm-reception').data.request;
}

/**
 * `POST /storage/requests/:id/report-not-received { reason }` — "No lo recibí": the API records the
 * reason (1–1000) and emails operations; the guardado stays contracted.
 */
export async function reportStorageNotReceived(requestId: number, reason: string): Promise<StorageGuardado> {
  const raw = await api.post<unknown>(`/storage/requests/${requestId}/report-not-received`, { reason });
  return parseResponse(StorageGuardadoResponseSchema, raw, 'POST /storage/requests/:id/report-not-received').data.request;
}

/**
 * NEX-1170 (X1) — `POST /storage/requests/:id/confirm-withdrawal` — "El cliente retiró sus cosas":
 * active|ending → ended (`endedBy: 'storage'`); the API emails the client and asks whoever has not
 * reviewed yet for a review. Only the depósito whose quote was accepted, `verified`.
 */
export async function confirmStorageWithdrawal(requestId: number): Promise<StorageGuardado> {
  const raw = await api.post<unknown>(`/storage/requests/${requestId}/confirm-withdrawal`, {});
  return parseResponse(StorageGuardadoResponseSchema, raw, 'POST /storage/requests/:id/confirm-withdrawal').data.request;
}

// --- Reviews of the guardado (NEX-1329, decision A6) ----------------------------------------------

/**
 * `POST /storage/requests/:id/reviews { rating, comment }` — the depósito rates the client of its
 * guardado, once, from the reception on: `rating` a whole 1–5, `comment` up to 1000 characters or
 * `null`. Refusals: 409 `STORAGE_REVIEW_NOT_ENABLED` (before the reception), 409
 * `REVIEW_ALREADY_SUBMITTED`, 409 `STORAGE_REQUEST_NOT_CONTRACTED`, 403 `STORAGE_GUARDADO_FORBIDDEN`.
 */
export async function submitStorageReview(
  requestId: number,
  input: { rating: number; comment: string | null }
): Promise<SubmittedStorageReview> {
  const raw = await api.post<unknown>(`/storage/requests/${requestId}/reviews`, input);
  return parseResponse(SubmitStorageReviewResponseSchema, raw, 'POST /storage/requests/:id/reviews').data.review;
}

// --- Profile (NEX-1164: "Mis depósitos" card titles) ------------------------------------------------

export type { StorageProfile };

/**
 * `GET /storage/me` — reused here ONLY for its `razonSocial` field, which `DepositosPage.tsx` needs
 * for its card titles (NEX-1164 design parity: "{razonSocial} · {locality}", matching the Claude
 * Design mockup, instead of just the old "{locality}, {department}").
 *
 * This is a SECOND, independent call to the exact same endpoint `src/pages/empresa/storage-api.ts`'s
 * own `getStorageProfile` already makes — deliberately NOT a cross-feature import of that function.
 * Root `AGENTS.md`'s coupling contract is explicit that a flat top-level page (`DepositosPage.tsx`
 * lives directly under `src/pages/`, not under a page-group folder) may import `src/shared/*` but
 * "should not" reach into another page's internals by relative path, and `src/pages/empresa/` is
 * exactly that — Milestone 2's own folder, with its own self-contained transport module for the
 * same concurrent-agents reason documented across this repo's `AGENTS.md` files (root + both
 * `src/shared/AGENTS.md` and `src/pages/empresa/AGENTS.md`: four transport modules exist precisely
 * because they were never meant to depend on one another). So this file — already one of the four
 * designated modules allowed to call `fetch`/read `env.VITE_API_URL` — is where a flat page's own
 * profile fetch belongs, not a new import across that boundary.
 *
 * Parses through the SAME shared `@/contracts/storage-profile` Zod schema `src/pages/empresa/
 * storage-api.ts` already uses (ground truth read from `nexvia-api-web`'s real
 * `get-storage-profile.handler.ts` — see that contract file's own docblock), so if the real wire
 * shape ever drifts, both independent call sites fail the same way instead of silently diverging —
 * the contracts layer, unlike the four transport modules themselves, is meant to be shared.
 *
 * Deliberately not exposed as anything richer than this one call: every other `StorageProfile` field
 * (`rut`, `approvalStatus`, documents, …) is "Mi empresa"'s own concern, not this screen's.
 */
export async function getStorageProfile(): Promise<StorageProfile> {
  const raw = await api.get<unknown>('/storage/me');
  return parseResponse(StorageProfileSchema, raw, 'GET /storage/me');
}

// --- Bank account (NEX-1169: the notice under the contracted quote) -------------------------------

/**
 * NEX-1169 (plan 2026-10-20, Tarea 1.10) — whether the depósito saved the bank account Nexvia pays
 * it into (`GET /storage/me/bank-account`: the account, or `null`). `CotizacionesPage` asks it only
 * when a quote was contracted, and `QuoteGuardado` then says "Cargá tu cuenta bancaria para que
 * podamos pagarte." A second, independent call to the endpoint "Mi empresa" edits through
 * `src/pages/empresa/storage-api.ts` — same reasoning as `getStorageProfile` above (a flat page
 * does not import a page group's transport); both parse through the same shared contract, which
 * reads this module's `{}` (what `api.ts` makes of a `null` body) as "no account".
 */
export async function hasStorageBankAccount(): Promise<boolean> {
  const raw = await api.get<unknown>('/storage/me/bank-account');
  return parseResponse(StorageBankAccountResponseSchema, raw, 'GET /storage/me/bank-account') !== null;
}

// --- Error copy -----------------------------------------------------------------------------------

/**
 * `ApiError.message` for every one of this module's endpoints is English, set directly in
 * nexvia-api-web/src/modules/storage/handlers/* (read against that module's source, 2026-10-13 —
 * e.g. create-storage-quote.handler.ts's `"This depósito already sent a quote for this request"`).
 * There is no es-UY copy to just forward, so it never reaches the UI as-is.
 *
 * Matched by `.code` (the stable per-error code nexvia-api-web's error.middleware.ts always
 * includes when the thrown error set one) rather than by parsing the English sentence — same
 * reasoning as this app's own useAuth.tsx's `describeAuthError` for Firebase's error codes. An
 * unrecognised or missing code falls back to a generic Spanish message instead of ever showing the
 * raw English string.
 */
const STORAGE_ERROR_MESSAGES: Record<string, string> = {
  STORAGE_ROLE_REQUIRED: 'Esta cuenta no es una cuenta de depósito.',
  STORAGE_PROFILE_NOT_FOUND: 'No encontramos tu perfil de depósito.',
  // NEX-1164 — answered by the inbox (read) to incomplete/rejected/suspended accounts and by quoting
  // to anything not verified, so it names neither and sends the account to Mi empresa.
  STORAGE_NOT_APPROVED: 'Tu cuenta de depósito no está habilitada para esto. Revisá el estado de tu alta y tu documentación en Mi empresa.',
  STORAGE_REQUEST_NOT_PUBLISHED: 'Esta solicitud ya no admite cotizaciones.',
  STORAGE_QUOTE_ALREADY_EXISTS: 'Ya enviaste una cotización para esta solicitud.',
  STORAGE_QUOTE_MONTHLY_PRICE_INVALID: 'El precio mensual es obligatorio y debe ser mayor a cero.',
  STORAGE_QUOTE_FULL_PERIOD_PRICE_INVALID: 'El precio por el período completo es obligatorio y debe ser mayor a cero.',
  // NEX-1165 — the quote's location: missing, or not one of this depósito's (also a deleted one).
  STORAGE_QUOTE_LOCATION_REQUIRED: 'Elegí la ubicación desde la que cotizás.',
  STORAGE_QUOTE_LOCATION_INVALID: 'Esa ubicación ya no está entre tus depósitos. Elegí otra o revisala en Mis depósitos.',
  // NEX-1165 (W2-hard-a, decision A1) — a quote goes out only from a location in the request's zone
  // (department + locality), and only for a request in a zone the depósito has a location in.
  STORAGE_QUOTE_LOCATION_OUT_OF_ZONE:
    'Esa ubicación no está en la zona de la solicitud. Elegí uno de tus depósitos del mismo departamento y localidad.',
  STORAGE_QUOTE_REQUEST_OUT_OF_ZONE:
    'Esta solicitud es de una zona donde no tenés depósitos. Para cotizarla, agregá un depósito en esa localidad desde Mis depósitos.',
  STORAGE_QUOTE_VALID_UNTIL_INVALID: 'La vigencia no es una fecha válida. Elegí otra fecha o dejala vacía.',
  // NEX-1170 — the guardado's lifecycle (nexvia-api-web `domain/storage-lifecycle.ts`,
  // `handlers/helpers/guardado-access.helper.ts`): a reception on a guardado no longer contracted
  // (confirmed meanwhile, by this panel in another tab or by Nexvia support), on a request with no
  // paid quote, or on a guardado that is another depósito's.
  STORAGE_STATUS_TRANSITION_INVALID: 'El guardado ya cambió de estado. Recargá la página para ver cómo está ahora.',
  STORAGE_REQUEST_NOT_CONTRACTED: 'Esta solicitud todavía no tiene un guardado contratado.',
  STORAGE_GUARDADO_FORBIDDEN: 'Este guardado no es de tu depósito: solo el depósito que el cliente contrató puede gestionarlo.',
  // NEX-1329 — the review of the client (`domain/guardado-review.ts`, `guardado-review.handler.ts`):
  // before the reception, or a second one (the transport code). STORAGE_REQUEST_NOT_CONTRACTED and
  // STORAGE_GUARDADO_FORBIDDEN above also answer it, and their copy fits it as is.
  STORAGE_REVIEW_NOT_ENABLED:
    'Todavía no podés calificar al cliente: la calificación se habilita cuando se confirma la recepción de sus cosas.',
  REVIEW_ALREADY_SUBMITTED: 'Ya calificaste al cliente por este guardado.',
  RESOURCE_NOT_FOUND: 'No encontramos esa solicitud — puede que ya no esté disponible.',
  VALIDATION_INVALID_ID: 'La solicitud indicada no es válida.',
  // Audit S6 — a text over its column/schema cap (the quote's conditions, a reception note, a review comment).
  VALIDATION_FIELD_TOO_LONG: 'Uno de los textos es demasiado largo. Acortalo y probá de nuevo.',
  // NEX-1645 — the module's flags (nexvia-api-web `require-storage-module.middleware.ts`, 503): turned
  // off, or "solo registro" on a quote. Reached when the state changed after this panel read
  // `GET /app/features` (`useStorageModuleState`), which otherwise closes these screens first.
  STORAGE_MODULE_DISABLED: STORAGE_MODULE_OFF_MESSAGE,
  STORAGE_REQUESTS_DISABLED: STORAGE_REQUESTS_DISABLED_MESSAGE,
  // Not a backend-issued code — this module's own `parseResponse` mints it when a 2xx body fails
  // the Zod contract (see that function's docblock). Mapped here like any other code so a
  // malformed response degrades to this banner instead of an uncaught crash.
  CONTRACT_VIOLATION: 'No pudimos procesar la respuesta del servidor. Intentá de nuevo en unos minutos.'
};

export function describeStorageError(err: unknown, fallback = 'No pudimos completar la acción. Intentá de nuevo.'): string {
  const code = err instanceof ApiError ? err.code : undefined;
  if (code && code in STORAGE_ERROR_MESSAGES) {
    return STORAGE_ERROR_MESSAGES[code];
  }
  // NEX-1164 — a session failure (`auth-failure.ts`: 401, or 403 AUTH_*/no code) says so; a domain
  // 403 (STORAGE_*) never reads as an expired session.
  if (err instanceof ApiError && isAuthFailure(err.status, code ? { code } : null)) {
    return 'Tu sesión expiró. Volvé a iniciar sesión.';
  }
  return fallback;
}

/** NEX-1164 — the inbox's answer to an account that may not read it yet (incomplete, rejected, suspended). */
export function isStorageNotApproved(err: unknown): boolean {
  return err instanceof ApiError && err.code === 'STORAGE_NOT_APPROVED';
}
